// CRM — embudo, contactos, tareas, notas y reportes (FASE C).
//
// Vive en su propia pagina y no como una seccion del portal, por lo mismo que el calendario: se abre
// y se deja abierta, y asi el sondeo de 10 s del portal no sigue corriendo debajo. La misma pagina se
// sirve en `crm.deviant.cl` y en `app.deviant.cl/crm.html` -- un solo build, dos vhosts sobre el
// mismo document root.
//
// ⚠️ **Nada se trae entero.** Un negocio con cientos de contactos rompe cualquier pantalla que pida
// "todo": los contactos vienen paginados con su total, el tablero trae los primeros de cada columna
// con el conteo real al lado, y ninguna vista carga hasta que alguien la abre.
//
// ⚠️ **Se proponen uniones, no se hacen solas.** La identidad que crea el bot esta verificada --el
// mensaje llego de ese numero--; la de una reserva es un telefono que alguien tecleo. Unir por un
// dato no verificado puede pegarle a un contacto el historial de conversacion de un desconocido.
import { apiFetch } from './api.js';
import { Toast } from '../comun/toast.js';
import { iniciarReporteDeErrores } from '../comun/errores.js';
import { montarBotonTema } from '../comun/tema.js';
import { telefonoLegible } from '../comun/telefono.js';
import { plural } from '../comun/texto.js';
import { montarCabecera } from '../comun/cabecera.js';
import './css/styles.css';
import './css/agenda.css';

iniciarReporteDeErrores();

const RAIZ = 'https://api.deviant.cl';
const API = {
  contactos: `${RAIZ}/portal/contactos`,
  contacto: `${RAIZ}/portal/contacto`,
  canales: `${RAIZ}/portal/crm/canales`,
  duplicados: `${RAIZ}/portal/contactos/duplicados`,
  fusionar: `${RAIZ}/portal/contactos/fusionar`,
  tablero: `${RAIZ}/portal/crm/tablero`,
  moverEtapa: `${RAIZ}/portal/crm/etapa`,
  guardarEtapa: `${RAIZ}/portal/crm/etapa/guardar`,
  ordenEtapas: `${RAIZ}/portal/crm/etapas/orden`,
  borrarEtapa: `${RAIZ}/portal/crm/etapa/borrar`,
  notas: `${RAIZ}/portal/crm/notas`,
  nota: `${RAIZ}/portal/crm/nota`,
  notaBorrar: `${RAIZ}/portal/crm/nota/borrar`,
  tareas: `${RAIZ}/portal/crm/tareas`,
  tarea: `${RAIZ}/portal/crm/tarea`,
  tareaEstado: `${RAIZ}/portal/crm/tarea/estado`,
  reportes: `${RAIZ}/portal/crm/reportes`,
  exportar: `${RAIZ}/portal/crm/exportar`,
  importar: `${RAIZ}/portal/crm/importar`,
  tokens: `${RAIZ}/portal/crm/tokens`,
  token: `${RAIZ}/portal/crm/token`,
  tokenRevocar: `${RAIZ}/portal/crm/token/revocar`,
  prioridad: `${RAIZ}/portal/crm/prioridad`,
  listas: `${RAIZ}/portal/crm/listas`,
  lista: `${RAIZ}/portal/crm/lista`,
  listaBorrar: `${RAIZ}/portal/crm/lista/borrar`,
  seguimiento: `${RAIZ}/portal/crm/seguimiento`,
  usuarios: `${RAIZ}/portal/usuarios`,
  logout: `${RAIZ}/portal/logout`,
};

/** Crear un nodo. Con `textContent` y no `innerHTML`: por aca pasan nombres que escribio gente. */
const el = (tag, clase, texto) => {
  const n = document.createElement(tag);
  if (clase) n.className = clase;
  if (texto !== undefined && texto !== null) n.textContent = texto;
  return n;
};
const $ = (id) => document.getElementById(id);
const icono = (nombre) => {
  const i = el('i', `ti ${nombre}`);
  i.setAttribute('aria-hidden', 'true');
  return i;
};

// El icono por canal deja distinguir de un vistazo por dónde llegó alguien sin leer una palabra.
// `ti-world` es el que se usa cuando el canal no tiene marca propia — nunca se inventa uno, porque
// `fuentes.sh` sólo mete en el subconjunto los que aparecen literales en el código.
const ICONO_CANAL = {
  whatsapp: 'ti-brand-whatsapp', telegram: 'ti-brand-telegram', email: 'ti-mail',
  web_chat: 'ti-message-2', instagram: 'ti-brand-instagram', messenger: 'ti-brand-messenger',
};
const iconoCanal = (c) => ICONO_CANAL[c] ?? 'ti-world';

const fecha = (d) => (d ? new Date(d).toLocaleString('es-CL') : '—');
const fechaCorta = (d) => (d
  ? new Date(d).toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : 'sin fecha');

const estado = {
  etapas: [],
  conteo: new Map(),
  usuarios: [],
  duplicados: [],
  canales: [],
  contactos: { pagina: 0, tamano: 50, total: 0 },
  tablero: { porEtapa: 20, q: '' },
  fichaAbierta: null,
  cargadas: new Set(),
};

/* ===========================================================================
   Navegacion
   =========================================================================== */

/**
 * Cada vista carga sus datos **la primera vez que se abre**, no al arrancar. Pedirlo todo de entrada
 * son seis consultas para mirar una pantalla, y en un negocio grande la mayoría no se abre nunca en
 * el día.
 */
async function irA(vista) {
  for (const b of document.querySelectorAll('.crm-menu-item[data-vista]')) {
    b.classList.toggle('activa', b.dataset.vista === vista);
  }
  for (const s of document.querySelectorAll('.crm-seccion')) {
    s.hidden = s.dataset.seccion !== vista;
  }
  // El hash deja recargar la página donde estabas y compartir un enlace a una vista.
  if (location.hash !== `#${vista}`) history.replaceState(null, '', `#${vista}`);

  const cargar = {
    resumen: cargarResumen, embudo: cargarTablero, contactos: cargarContactos,
    tareas: cargarTareas, reportes: cargarReportes, etapas: pintarEtapas,
    prioridad: cargarPrioridad,
    datos: cargarDatos,
  }[vista];
  if (!cargar) return;
  // Las tres que cambian con lo que uno hace se recargan siempre; el tablero y las etapas viven en
  // memoria y se repintan solos cuando algo los toca.
  if (['resumen', 'contactos', 'tareas', 'reportes', 'datos', 'prioridad'].includes(vista)
      || !estado.cargadas.has(vista)) {
    estado.cargadas.add(vista);
    await cargar();
  }
}

/* ===========================================================================
   Resumen
   =========================================================================== */

function tarjetaCifra(valor, etiqueta, ico, tono) {
  const t = el('div', `crm-tarjeta${tono ? ` crm-tarjeta-${tono}` : ''}`);
  t.appendChild(icono(ico));
  const d = el('div');
  d.appendChild(el('div', 'crm-tarjeta-n', String(valor)));
  d.appendChild(el('div', 'crm-tarjeta-txt', etiqueta));
  t.appendChild(d);
  return t;
}

async function cargarResumen() {
  const cifras = $('res-cifras');
  cifras.innerHTML = '';

  let tareas = [];
  let total = 0;
  let sinEtapa = 0;
  try {
    const [rt, rc, rs] = await Promise.all([
      apiFetch(API.tareas, { credentials: 'include', hideErrorToast: true }),
      apiFetch(`${API.contactos}?limite=1`, { credentials: 'include', hideErrorToast: true }),
      apiFetch(`${API.contactos}?limite=1&etapa=sin`, { credentials: 'include', hideErrorToast: true }),
    ]);
    tareas = rt.tareas ?? [];
    total = rc.total ?? 0;
    sinEtapa = rs.total ?? 0;
  } catch { /* lo que no se pueda contar se muestra en cero, nunca se estima */ }

  const vencidas = tareas.filter((t) => t.vencida).length;
  cifras.append(
    tarjetaCifra(total, 'contactos', 'ti-users'),
    // Los sin clasificar van aparte porque es la cifra que se quiere bajar, y la única que crece
    // sola sin que nadie haga nada.
    tarjetaCifra(sinEtapa, 'sin etapa', 'ti-tag', sinEtapa > 0 ? 'aviso' : null),
    tarjetaCifra(tareas.length, 'tareas abiertas', 'ti-clipboard-list'),
    tarjetaCifra(vencidas, 'vencidas', 'ti-clock', vencidas > 0 ? 'malo' : 'bueno'),
  );
  $('badge-contactos').textContent = total ? String(total) : '';
  $('badge-tareas').textContent = tareas.length ? String(tareas.length) : '';

  // En el resumen sólo lo vencido y lo de hoy: la lista completa acá sería la vista de tareas
  // repetida, y la gracia de esta pantalla es que se termina de leer.
  const finDeHoy = new Date();
  finDeHoy.setHours(23, 59, 59, 999);
  const urgentes = tareas.filter((t) => t.vencida
    || (t.vence_en && new Date(t.vence_en) <= finDeHoy));
  const caja = $('res-tareas');
  caja.innerHTML = '';
  if (!urgentes.length) {
    caja.appendChild(el('p', 'agente-ayuda', `Nada vencido ni para hoy.${
      tareas.length ? ` Tienes ${plural(tareas.length, 'tarea', 'tareas')} más adelante.` : ''}`));
  } else {
    for (const t of urgentes) caja.appendChild(filaTarea(t));
  }

  try {
    estado.duplicados = (await apiFetch(API.duplicados,
      { credentials: 'include', hideErrorToast: true })).pares ?? [];
  } catch { estado.duplicados = []; }
  pintarDuplicados();
}

function pintarDuplicados() {
  const bloque = $('res-dup-bloque');
  const caja = $('res-duplicados');
  caja.innerHTML = '';
  bloque.hidden = !estado.duplicados.length;
  if (!estado.duplicados.length) return;

  const t = el('div', 'agente-aviso');
  t.appendChild(icono('ti-alert-triangle'));
  const txt = el('div');
  txt.appendChild(el('strong', '', 'Estas fichas podrían ser la misma persona.'));
  txt.appendChild(document.createTextNode(
    ' Al unirlas, sus conversaciones y sus horas quedan juntas. No se puede deshacer.'));
  t.appendChild(txt);
  caja.appendChild(t);

  for (const p of estado.duplicados) {
    const fila = el('div', 'cfg-item');
    const izq = el('div');
    izq.appendChild(el('div', 'cfg-item-titulo', `${p.a_nombre}  ·  ${p.b_nombre}`));
    izq.appendChild(el('div', 'cfg-item-sub', p.motivo));
    fila.appendChild(izq);
    const der = el('div', 'cfg-item-acciones');
    // Se ofrece explícitamente cuál queda: "unir" sin decir hacia dónde deja al usuario adivinando
    // qué nombre y qué teléfono sobreviven.
    for (const [principal, secundario, nombre] of
      [[p.a_id, p.b_id, p.a_nombre], [p.b_id, p.a_id, p.b_nombre]]) {
      const b = el('button', 'btn-sec btn-sm', `Dejar «${nombre}»`);
      b.type = 'button';
      b.addEventListener('click', () => fusionar(principal, secundario, nombre));
      der.appendChild(b);
    }
    fila.appendChild(der);
    caja.appendChild(fila);
  }
}

async function fusionar(principal, secundario, nombre) {
  if (!window.confirm(`¿Unir las dos fichas y dejar «${nombre}»? No se puede deshacer.`)) return;
  try {
    await apiFetch(API.fusionar, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ principal, secundario }),
      hideErrorToast: true,
    });
    Toast.success('Fichas unidas.');
    await cargarTablero();
    await cargarResumen();
  } catch (e) { Toast.error(e?.mensaje || 'No se pudo unir.'); }
}

/* ===========================================================================
   Contactos
   =========================================================================== */

function filtrosContactos() {
  const p = new URLSearchParams();
  const q = $('con-buscar').value.trim();
  if (q) p.set('q', q);
  if ($('con-etapa').value) p.set('etapa', $('con-etapa').value);
  if ($('con-canal').value) p.set('canal', $('con-canal').value);
  if ($('con-citas').value) p.set('citas', $('con-citas').value);
  p.set('orden', $('con-orden').value);
  p.set('limite', String(estado.contactos.tamano));
  p.set('desde', String(estado.contactos.pagina * estado.contactos.tamano));
  return p;
}

function filaContacto(c) {
  const fila = el('div', 'cfg-item');
  const izq = el('div');
  const t = el('div', 'cfg-item-titulo');
  t.appendChild(el('span', '', c.nombre));
  if (c.etapa) {
    const chip = el('span', 'crm-chip', c.etapa);
    if (c.etapa_color) chip.style.setProperty('--chip', c.etapa_color);
    t.appendChild(chip);
  }
  izq.appendChild(t);
  const partes = [telefonoLegible(c.telefono), c.email].filter(Boolean);
  // Plural de verdad y no «(es)»: «1 conversación(es) · 0 hora(s)» lo escribe un programa, no una
  // persona, y las horas en cero no aportan nada --el que no tiene horas no necesita que se lo
  // digan en cada fila--.
  partes.push(plural(c.conversaciones, 'conversación', 'conversaciones'));
  if (c.citas > 0) partes.push(plural(c.citas, 'hora', 'horas'));
  izq.appendChild(el('div', 'cfg-item-sub', partes.join(' · ')));
  fila.appendChild(izq);
  const der = el('div', 'cfg-item-acciones');
  const b = el('button', 'btn-sec btn-sm', 'Ver ficha');
  b.type = 'button';
  b.addEventListener('click', () => verFicha(c.id));
  der.appendChild(b);
  fila.appendChild(der);
  return fila;
}

async function cargarContactos() {
  const caja = $('con-lista');
  caja.textContent = 'Buscando…';
  let r;
  try {
    r = await apiFetch(`${API.contactos}?${filtrosContactos()}`, { credentials: 'include' });
  } catch { caja.textContent = 'No se pudo cargar.'; return; }

  estado.contactos.total = r.total ?? 0;
  caja.innerHTML = '';
  if (!r.contactos.length) {
    caja.appendChild(el('p', 'agente-ayuda',
      estado.contactos.pagina > 0 ? 'No hay más.' : 'Nadie con esos filtros.'));
  }
  for (const c of r.contactos) caja.appendChild(filaContacto(c));

  const desde = estado.contactos.pagina * estado.contactos.tamano;
  const hasta = desde + r.contactos.length;
  // ⚠️ El total va a la vista: "50 de 400" y "50" son dos pantallas distintas, y sin el total nadie
  // sabe que el contacto que busca está tres páginas más allá.
  $('con-rango').textContent = estado.contactos.total
    ? `${desde + 1}–${hasta} de ${estado.contactos.total}` : '';
  $('con-antes').disabled = estado.contactos.pagina === 0;
  $('con-despues').disabled = hasta >= estado.contactos.total;
  $('badge-contactos').textContent = estado.contactos.total ? String(estado.contactos.total) : '';
}

function llenarFiltros() {
  const se = $('con-etapa');
  const antes = se.value;
  se.innerHTML = '';
  const todo = el('option', '', 'Todas las etapas'); todo.value = '';
  const sin = el('option', '', 'Sin etapa'); sin.value = 'sin';
  se.append(todo, sin);
  for (const e of estado.etapas) {
    const o = el('option', '', e.nombre); o.value = e.id;
    se.appendChild(o);
  }
  se.value = antes;

  const sc = $('con-canal');
  if (sc.options.length) return;   // los canales no cambian mientras la pantalla está abierta
  const tc = el('option', '', 'Todos los canales'); tc.value = '';
  sc.appendChild(tc);
  for (const c of estado.canales) {
    const o = el('option', '', `${c.tipo_canal} (${c.contactos})`); o.value = c.tipo_canal;
    sc.appendChild(o);
  }
}

/* ===========================================================================
   Tablero
   ===========================================================================
   Un kanban y no una tabla con una columna "etapa": mover a alguien de etapa es la accion que se
   hace veinte veces al dia en un CRM, y en una tabla son tres clics y un desplegable cada vez.

   ⚠️ **El arrastre no puede ser la unica forma de mover.** HTML5 drag-and-drop no existe en tactil,
   y ademas hay quien navega con teclado: el selector de etapa de la ficha sigue haciendo lo mismo. */

/** La columna de los sin clasificar. No es una etapa, y por eso no se puede editar ni borrar. */
const SIN_ETAPA = '__sin__';

function tarjetaContacto(c) {
  const t = el('article', 'crm-tarj');
  t.draggable = true;
  t.dataset.contacto = c.contacto_id;
  t.appendChild(el('div', 'crm-tarj-nombre', c.nombre));
  const meta = [telefonoLegible(c.telefono),
    c.citas > 0 ? plural(c.citas, 'hora', 'horas') : null].filter(Boolean);
  if (meta.length) t.appendChild(el('div', 'crm-tarj-meta', meta.join(' · ')));
  t.addEventListener('dragstart', (ev) => {
    // Dos tipos distintos de carga: así una columna arrastrada nunca se suelta como si fuera una
    // persona, ni al revés. Con un solo tipo, el `drop` tendría que adivinar.
    ev.dataTransfer.setData('text/x-contacto', c.contacto_id);
    ev.dataTransfer.effectAllowed = 'move';
    t.classList.add('crm-arrastrando');
  });
  t.addEventListener('dragend', () => t.classList.remove('crm-arrastrando'));
  t.addEventListener('click', () => verFicha(c.contacto_id));
  return t;
}

function columna(etapa, cuantos) {
  const esSin = etapa.id === SIN_ETAPA;
  const col = el('section', 'crm-col');
  col.dataset.etapa = etapa.id;
  if (etapa.color) col.style.setProperty('--col', etapa.color);

  const cab = el('header', 'crm-col-cab');
  // Sólo las etapas de verdad se reordenan: "Sin etapa" no tiene orden que guardar.
  cab.draggable = !esSin;
  const titulo = el('div', 'crm-col-titulo');
  titulo.appendChild(el('span', 'crm-col-nombre', etapa.nombre));
  titulo.appendChild(el('span', 'crm-col-n', String(cuantos)));
  cab.appendChild(titulo);

  const sub = [];
  if (etapa.tipo && etapa.tipo !== 'abierta') sub.push(etapa.tipo === 'ganada' ? 'Cierre ganado' : 'Cierre perdido');
  if (etapa.automatica === 'agendada') sub.push('automática al agendar');
  if (etapa.automatica === 'atendida') sub.push('automática al asistir');
  if (sub.length) cab.appendChild(el('div', 'crm-col-sub', sub.join(' · ')));

  if (!esSin) {
    const b = el('button', 'crm-col-editar');
    b.type = 'button'; b.title = 'Editar etapa'; b.setAttribute('aria-label', `Editar ${etapa.nombre}`);
    b.appendChild(icono('ti-adjustments'));
    b.addEventListener('click', (ev) => { ev.stopPropagation(); abrirEditor(etapa, 'crm-editor'); });
    cab.appendChild(b);

    cab.addEventListener('dragstart', (ev) => {
      ev.dataTransfer.setData('text/x-etapa', etapa.id);
      ev.dataTransfer.effectAllowed = 'move';
      col.classList.add('crm-arrastrando');
    });
    cab.addEventListener('dragend', () => col.classList.remove('crm-arrastrando'));
  }
  col.appendChild(cab);

  const cuerpo = el('div', 'crm-col-cuerpo');
  col.appendChild(cuerpo);

  col.addEventListener('dragover', (ev) => {
    // Sin `preventDefault` el navegador no considera esto un destino válido y el `drop` no llega.
    ev.preventDefault();
    ev.dataTransfer.dropEffect = 'move';
    col.classList.add('crm-col-sobre');
  });
  col.addEventListener('dragleave', () => col.classList.remove('crm-col-sobre'));
  col.addEventListener('drop', (ev) => {
    ev.preventDefault();
    col.classList.remove('crm-col-sobre');
    const contacto = ev.dataTransfer.getData('text/x-contacto');
    if (contacto) { moverEtapa(contacto, esSin ? '' : etapa.id); return; }
    const movida = ev.dataTransfer.getData('text/x-etapa');
    if (movida && movida !== etapa.id && !esSin) reordenar(movida, etapa.id);
  });
  return { col, cuerpo };
}

function pintarTablero(contactos) {
  const caja = $('crm-embudo');
  caja.innerHTML = '';
  const porEtapa = new Map();
  for (const c of contactos) {
    const k = c.etapa_id ?? SIN_ETAPA;
    if (!porEtapa.has(k)) porEtapa.set(k, []);
    porEtapa.get(k).push(c);
  }
  // Los sin clasificar van primero y **siempre se dibujan**, aunque estén vacíos: un negocio que
  // recién abre el CRM tiene ahí a todo el mundo, y esconder la columna sería mostrarle un tablero
  // vacío teniendo gente adentro.
  const sin = { id: SIN_ETAPA, nombre: 'Sin etapa' };
  for (const e of [sin, ...estado.etapas]) {
    const gente = porEtapa.get(e.id) ?? [];
    // El conteo real, no el de las tarjetas dibujadas: una columna que dice "20" teniendo 340 es
    // peor que no decir nada. Con filtro puesto se cuenta lo que se ve, que es lo que se preguntó.
    const cuantos = estado.tablero.q
      ? gente.length
      : (estado.conteo.get(e.id === SIN_ETAPA ? null : e.id) ?? 0);
    const { col, cuerpo } = columna(e, cuantos);
    if (!gente.length) cuerpo.appendChild(el('p', 'crm-col-vacia', 'Arrastra a alguien acá.'));
    for (const c of gente) cuerpo.appendChild(tarjetaContacto(c));
    if (cuantos > gente.length) {
      const mas = el('button', 'crm-col-mas', `Ver más (${cuantos - gente.length})`);
      mas.type = 'button';
      mas.addEventListener('click', () => {
        estado.tablero.porEtapa += 20;
        cargarTablero();
      });
      cuerpo.appendChild(mas);
    }
    caja.appendChild(col);
  }
}

async function cargarTablero() {
  const p = new URLSearchParams({ porEtapa: String(estado.tablero.porEtapa) });
  if (estado.tablero.q) p.set('q', estado.tablero.q);
  let r;
  try {
    r = await apiFetch(`${API.tablero}?${p}`, { credentials: 'include', hideErrorToast: true });
  } catch { $('crm-embudo').textContent = 'No se pudo cargar el tablero.'; return; }
  estado.etapas = r.etapas ?? [];
  estado.conteo = new Map((r.conteo ?? []).map((x) => [x.etapa_id, Number(x.contactos)]));
  llenarFiltros();
  pintarTablero(r.contactos ?? []);
  pintarEtapas();
}

/** Mover de etapa. Se usa desde el arrastre y desde el selector de la ficha. */
async function moverEtapa(contactoId, etapaId) {
  try {
    await apiFetch(API.moverEtapa, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contacto: contactoId, etapa: etapaId || null }),
      hideErrorToast: true,
    });
    Toast.success('Etapa actualizada.');
  } catch (e) { Toast.error(e?.mensaje || 'No se pudo mover.'); }
  await cargarTablero();
  if (estado.fichaAbierta === contactoId) await verFicha(contactoId);
}

/**
 * Reordenar: se manda **la lista completa** en el orden nuevo, no "sube uno". Con movimientos
 * relativos, dos personas arrastrando a la vez dejan el tablero en un orden que ninguna eligió.
 */
async function reordenar(movidaId, destinoId) {
  const ids = estado.etapas.map((e) => e.id).filter((id) => id !== movidaId);
  const i = ids.indexOf(destinoId);
  ids.splice(i < 0 ? ids.length : i, 0, movidaId);
  try {
    await apiFetch(API.ordenEtapas, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
      hideErrorToast: true,
    });
  } catch (e) { Toast.error(e?.mensaje || 'No se pudo reordenar.'); }
  // Se repinta desde el servidor: es él quien manda sobre el orden que quedó guardado.
  await cargarTablero();
}

/* ===========================================================================
   Etapas
   =========================================================================== */

function pintarEtapas() {
  const caja = $('eta-lista');
  if (!caja) return;
  caja.innerHTML = '';
  if (!estado.etapas.length) {
    caja.appendChild(el('p', 'agente-ayuda', 'Todavía no hay etapas. Se crean solas al abrir el embudo.'));
    return;
  }
  for (const e of estado.etapas) {
    const fila = el('div', 'cfg-item');
    const izq = el('div', 'crm-eta-fila');
    const punto = el('span', 'crm-punto');
    punto.style.setProperty('--chip', e.color || '#5549FF');
    izq.appendChild(punto);
    const txt = el('div');
    txt.appendChild(el('div', 'cfg-item-titulo', e.nombre));
    const sub = [plural(estado.conteo.get(e.id) ?? 0, 'contacto', 'contactos')];
    if (e.tipo !== 'abierta') sub.push(e.tipo === 'ganada' ? 'cierre ganado' : 'cierre perdido');
    if (e.automatica === 'agendada') sub.push('automática al agendar');
    if (e.automatica === 'atendida') sub.push('automática al asistir');
    txt.appendChild(el('div', 'cfg-item-sub', sub.join(' · ')));
    izq.appendChild(txt);
    fila.appendChild(izq);
    const der = el('div', 'cfg-item-acciones');
    const b = el('button', 'btn-sec btn-sm', 'Editar');
    b.type = 'button';
    b.addEventListener('click', () => abrirEditor(e, 'crm-editor-etapas'));
    der.appendChild(b);
    fila.appendChild(der);
    caja.appendChild(fila);
  }
}

/** El editor de una etapa. Un panel y no un `prompt()`: son cuatro decisiones, no un nombre. */
function abrirEditor(etapa, contenedor) {
  const caja = $(contenedor);
  caja.hidden = false;
  caja.innerHTML = '';
  const form = el('form', 'cfg-tarjeta');
  const cab = el('div', 'cfg-tarjeta-cab');
  cab.appendChild(icono('ti-adjustments'));
  const txt = el('div');
  txt.appendChild(el('h3', '', etapa ? `Etapa «${etapa.nombre}»` : 'Nueva etapa'));
  txt.appendChild(el('p', '', 'Un paso de tu forma de vender. El orden se cambia arrastrando las columnas del tablero.'));
  cab.appendChild(txt);
  form.appendChild(cab);

  // Los campos van en la rejilla que ya usa el resto de la configuración, y cada uno dentro de un
  // `ag-campo`: es el envoltorio que le da estilo al control. Un `<select class="input">` no se
  // pinta con nada — esa clase no existe en el portal.
  const campos = el('div', 'cfg-campos');
  form.appendChild(campos);
  const campo = (etiqueta, ctl, ayuda) => {
    const f = el('label', 'ag-campo');
    f.appendChild(el('span', '', etiqueta));
    f.appendChild(ctl);
    if (ayuda) f.appendChild(el('p', 'agente-ayuda', ayuda));
    campos.appendChild(f);
  };

  const nombre = document.createElement('input');
  nombre.type = 'text'; nombre.required = true; nombre.maxLength = 60;
  nombre.value = etapa?.nombre ?? '';
  campo('Nombre', nombre);

  const tipo = document.createElement('select');
  for (const [v, t] of [['abierta', 'En curso'], ['ganada', 'Cierre ganado'], ['perdida', 'Cierre perdido']]) {
    const o = el('option', '', t); o.value = v;
    if ((etapa?.tipo ?? 'abierta') === v) o.selected = true;
    tipo.appendChild(o);
  }
  campo('Tipo', tipo, 'Las dos de cierre dicen cómo terminó.');

  const color = document.createElement('input');
  color.type = 'color'; color.value = etapa?.color ?? '#5549FF';
  campo('Color', color);

  const auto = document.createElement('select');
  for (const [v, t] of [['', 'No, se mueve a mano'],
    ['agendada', 'Cuando alguien agenda una hora'],
    ['atendida', 'Cuando alguien asiste a su hora']]) {
    const o = el('option', '', t); o.value = v;
    if ((etapa?.automatica ?? '') === v) o.selected = true;
    auto.appendChild(o);
  }
  campo('Mover gente acá sola', auto,
    'Mueve la ficha sola. Cada automatismo lo lleva una sola etapa: al elegirlo acá, se le quita a la que lo tenga.');

  // Orden: lo destructivo lejos y a la izquierda, lo que se aprieta siempre al final a la derecha.
  const acciones = el('div', 'cfg-guardar');
  if (etapa) {
    const borrar = el('button', 'btn-sec', 'Eliminar');
    borrar.type = 'button';
    borrar.addEventListener('click', () => borrarEtapa(etapa, contenedor));
    acciones.appendChild(borrar);
  }
  const cerrar = el('button', 'btn-sec', 'Cancelar');
  cerrar.type = 'button';
  cerrar.addEventListener('click', () => { caja.hidden = true; caja.innerHTML = ''; });
  acciones.appendChild(cerrar);
  const guardar = el('button', 'ag-accion', 'Guardar');
  guardar.type = 'submit';
  acciones.appendChild(guardar);
  form.appendChild(acciones);

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    guardar.disabled = true;
    try {
      await apiFetch(API.guardarEtapa, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: etapa?.id ?? null, nombre: nombre.value, tipo: tipo.value,
          color: color.value, automatica: auto.value || null,
        }),
        hideErrorToast: true,
      });
      Toast.success('Etapa guardada.');
      caja.hidden = true; caja.innerHTML = '';
      await cargarTablero();
    } catch (e) { Toast.error(e?.mensaje || 'No se pudo guardar.'); guardar.disabled = false; }
  });
  caja.appendChild(form);
  nombre.focus();
  caja.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

async function borrarEtapa(etapa, contenedor) {
  const n = estado.conteo.get(etapa.id) ?? 0;
  const aviso = n > 0
    ? `¿Eliminar «${etapa.nombre}»? ${n} contacto(s) quedan sin etapa; no se borra a nadie.`
    : `¿Eliminar «${etapa.nombre}»?`;
  if (!window.confirm(aviso)) return;
  try {
    const r = await apiFetch(API.borrarEtapa, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: etapa.id }),
      hideErrorToast: true,
    });
    Toast.success(r.movidos ? `Etapa eliminada. ${r.movidos} contacto(s) quedaron sin etapa.` : 'Etapa eliminada.');
    const caja = $(contenedor);
    caja.hidden = true; caja.innerHTML = '';
    await cargarTablero();
  } catch (e) { Toast.error(e?.mensaje || 'No se pudo eliminar.'); }
}

/* ===========================================================================
   Tareas
   =========================================================================== */

/** Una fila de tarea. La casilla cierra; el resto es lectura. */
function filaTarea(t, { mostrarContacto = true } = {}) {
  const fila = el('div', 'cfg-item');
  const izq = el('div', 'crm-tarea-txt');

  const marca = document.createElement('input');
  marca.type = 'checkbox';
  marca.className = 'crm-tarea-check';
  marca.checked = t.estado !== 'pendiente';
  marca.title = 'Marcar como hecha';
  marca.addEventListener('change', () => cerrarTarea(t.id, marca.checked ? 'hecha' : 'pendiente'));

  // ⚠️ La casilla va dentro de un `<label>` **por el area que se puede tocar**: un
  // `input[type=checkbox]` mide 22 px y no hay forma de agrandarle la caja desde el CSS --el
  // navegador ignora `border` y `padding` en un control nativo, comprobado--, asi que el toque
  // seguia siendo de 22. Un `<label>` que lo contiene si acepta medidas, y tocarlo alterna la
  // casilla sin una sola linea de JS. Importa aca mas que en otras pantallas: errarle a esta
  // casilla marca **hecha** la tarea de al lado.
  const toque = el('label', 'crm-tarea-toque');
  toque.appendChild(marca);

  const titulo = el('div', 'cfg-item-titulo', t.titulo);
  if (t.estado !== 'pendiente') titulo.classList.add('crm-tarea-cerrada');
  izq.appendChild(titulo);

  const partes = [t.vence_en ? fechaCorta(t.vence_en) : 'sin fecha'];
  if (mostrarContacto && t.contacto) partes.push(t.contacto);
  if (t.responsable && !t.propia) partes.push(`para ${t.responsable}`);
  if (t.recordar_en) partes.push('con aviso');
  if (t.estado === 'cancelada') partes.push('cancelada');
  // Lo vencido además se marca: el color es lo único que se lee de reojo en una lista de veinte.
  const sub = el('div', 'cfg-item-sub', partes.join(' · '));
  if (t.vencida) sub.classList.add('crm-vencida');
  izq.appendChild(sub);

  const cont = el('div', 'crm-tarea-fila');
  cont.append(toque, izq);
  fila.appendChild(cont);

  const der = el('div', 'cfg-item-acciones');
  if (t.estado === 'pendiente') {
    const ed = el('button', 'btn-sec btn-sm', 'Editar');
    ed.type = 'button';
    ed.addEventListener('click', () => abrirEditorTarea(t, t.contacto_id));
    const ca = el('button', 'btn-sec btn-sm', 'Cancelar');
    ca.type = 'button';
    ca.addEventListener('click', () => cerrarTarea(t.id, 'cancelada'));
    der.append(ed, ca);
  }
  fila.appendChild(der);
  return fila;
}

async function cargarTareas() {
  const caja = $('tar-lista');
  const p = new URLSearchParams({
    mias: $('tar-quien').value, cerradas: String($('tar-cerradas').checked),
  });
  let r;
  try {
    r = await apiFetch(`${API.tareas}?${p}`, { credentials: 'include', hideErrorToast: true });
  } catch { caja.textContent = 'No se pudieron cargar las tareas.'; return; }
  caja.innerHTML = '';
  if (!r.tareas.length) {
    caja.appendChild(el('p', 'agente-ayuda', 'Nada pendiente. Las tareas que crees aparecen acá.'));
    return;
  }
  for (const t of r.tareas) caja.appendChild(filaTarea(t));
}

async function cerrarTarea(id, estadoNuevo) {
  try {
    await apiFetch(API.tareaEstado, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, estado: estadoNuevo }),
      hideErrorToast: true,
    });
  } catch (e) { Toast.error(e?.mensaje || 'No se pudo actualizar.'); }
  await cargarTareas();
  await cargarResumen();
  if (estado.fichaAbierta) await verFicha(estado.fichaAbierta);
}

/**
 * El editor de una tarea. El aviso se elige en palabras («1 hora antes») y no como una segunda fecha
 * que hay que calcular a mano: dos campos de fecha para una sola cosa es donde la gente se equivoca.
 */
async function abrirEditorTarea(tarea, contactoId) {
  await irA('tareas');
  const caja = $('crm-tarea-editor');
  caja.hidden = false;
  caja.innerHTML = '';
  const form = el('form', 'cfg-tarjeta');
  const cab = el('div', 'cfg-tarjeta-cab');
  cab.appendChild(icono('ti-clipboard-list'));
  const txt = el('div');
  txt.appendChild(el('h3', '', tarea ? 'Editar tarea' : 'Nueva tarea'));
  txt.appendChild(el('p', '', 'Quién la hace y para cuándo. El aviso llega al navegador.'));
  cab.appendChild(txt);
  form.appendChild(cab);

  const campos = el('div', 'cfg-campos');
  form.appendChild(campos);
  const campo = (etiqueta, ctl, ayuda) => {
    const f = el('label', 'ag-campo');
    f.appendChild(el('span', '', etiqueta));
    f.appendChild(ctl);
    if (ayuda) f.appendChild(el('p', 'agente-ayuda', ayuda));
    campos.appendChild(f);
  };

  const titulo = document.createElement('input');
  titulo.type = 'text'; titulo.required = true; titulo.maxLength = 160;
  titulo.value = tarea?.titulo ?? '';
  campo('Qué hay que hacer', titulo);

  const vence = document.createElement('input');
  vence.type = 'datetime-local';
  if (tarea?.vence_en) {
    // `datetime-local` no entiende una fecha con zona: hay que darle la hora local ya recortada.
    const d = new Date(tarea.vence_en);
    vence.value = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  }
  campo('Para cuándo', vence, 'Sin fecha queda al final de la lista, sin aviso.');

  const aviso = document.createElement('select');
  for (const [v, t] of [['', 'No avisar'], ['0', 'A la hora'], ['60', '1 hora antes'],
    ['180', '3 horas antes'], ['1440', 'El día antes']]) {
    const o = el('option', '', t); o.value = v;
    aviso.appendChild(o);
  }
  if (tarea?.recordar_en && tarea?.vence_en) {
    const min = Math.round((new Date(tarea.vence_en) - new Date(tarea.recordar_en)) / 60000);
    if ([...aviso.options].some((o) => o.value === String(min))) aviso.value = String(min);
  }
  campo('Avisar', aviso);

  const resp = document.createElement('select');
  for (const u of estado.usuarios) {
    const o = el('option', '', `${u.nombre ?? ''} ${u.apellido ?? ''}`.trim() || u.email_login);
    o.value = u.id;
    if (tarea?.responsable_id === u.id) o.selected = true;
    resp.appendChild(o);
  }
  campo('Responsable', resp, 'Una tarea de todos es una tarea de nadie.');

  const acciones = el('div', 'cfg-guardar');
  const cerrar = el('button', 'btn-sec', 'Cancelar');
  cerrar.type = 'button';
  cerrar.addEventListener('click', () => { caja.hidden = true; caja.innerHTML = ''; });
  const guardar = el('button', 'ag-accion', 'Guardar');
  guardar.type = 'submit';
  acciones.append(cerrar, guardar);
  form.appendChild(acciones);

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    if (aviso.value !== '' && !vence.value) { Toast.error('Para avisar hace falta una fecha.'); return; }
    guardar.disabled = true;
    const venceISO = vence.value ? new Date(vence.value).toISOString() : null;
    const recordar = (aviso.value !== '' && venceISO)
      ? new Date(new Date(venceISO).getTime() - Number(aviso.value) * 60000).toISOString()
      : null;
    try {
      await apiFetch(API.tarea, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: tarea?.id ?? null, titulo: titulo.value, contacto: contactoId ?? null,
          vence: venceISO, recordar, responsable: resp.value || null,
        }),
        hideErrorToast: true,
      });
      Toast.success('Tarea guardada.');
      caja.hidden = true; caja.innerHTML = '';
      await cargarTareas();
    } catch (e) { Toast.error(e?.mensaje || 'No se pudo guardar.'); guardar.disabled = false; }
  });
  caja.appendChild(form);
  titulo.focus();
}

/* ===========================================================================
   Reportes (C.5)
   =========================================================================== */

/** Una barra proporcional. Sin librería: no puede entrar nada de un CDN (tarea 4.13). */
function barra(etiqueta, valor, maximo, extra, color) {
  const f = el('div', 'crm-barra-fila');
  const cab = el('div', 'crm-barra-cab');
  const txt = el('span', 'crm-barra-txt');
  txt.appendChild(el('span', '', etiqueta));
  cab.appendChild(txt);
  cab.appendChild(el('span', 'crm-barra-n', extra ?? String(valor)));
  f.appendChild(cab);
  const pista = el('div', 'crm-barra');
  const relleno = el('div', `crm-barra-llena${valor > 0 ? '' : ' crm-barra-cero'}`);
  relleno.style.width = `${maximo > 0 ? Math.round((valor / maximo) * 100) : 0}%`;
  if (color) relleno.style.background = color;
  pista.appendChild(relleno);
  f.appendChild(pista);
  return f;
}

function bloqueReporte(titulo, ico, descripcion) {
  const b = el('div', 'crm-bloque');
  const h = el('h2');
  h.appendChild(icono(ico));
  h.appendChild(document.createTextNode(` ${titulo}`));
  b.appendChild(h);
  if (descripcion) b.appendChild(el('p', 'agente-ayuda', descripcion));
  return b;
}

async function cargarReportes() {
  const caja = $('rep-todo');
  caja.textContent = 'Calculando…';
  let r;
  try {
    r = await apiFetch(`${API.reportes}?dias=${$('rep-dias').value}`,
      { credentials: 'include', hideErrorToast: true });
  } catch { caja.textContent = 'No se pudieron calcular los reportes.'; return; }
  caja.innerHTML = '';

  // --- Embudo ---
  const bE = bloqueReporte('Por etapa', 'ti-tag',
    'Cuánta gente hay en cada etapa, cuántos entraron y cuánto tardan en salir.');
  const maxE = Math.max(1, ...r.etapas.map((e) => Math.max(Number(e.actuales), Number(e.entraron))));
  for (const e of r.etapas) {
    const dias = e.dias_promedio !== null ? `${e.dias_promedio} días de media` : 'sin salidas aún';
    bE.appendChild(barra(e.nombre, Number(e.actuales), maxE,
      `${e.actuales} ahora · ${e.entraron} entraron · ${dias}`, e.color));
  }
  if (!r.etapas.length) bE.appendChild(el('p', 'agente-ayuda', 'Todavía no hay etapas.'));
  caja.appendChild(bE);

  // --- Canal ---
  const bC = bloqueReporte('Por canal', 'ti-plug-connected',
    'Por dónde escribe la gente, y cuántos de ésos llegaron a agendar.');
  const maxC = Math.max(1, ...r.canales.map((c) => Number(c.contactos)));
  for (const c of r.canales) {
    const pct = Number(c.contactos) ? Math.round((Number(c.con_hora) / Number(c.contactos)) * 100) : 0;
    const fila = barra(c.canal, Number(c.contactos), maxC,
      `${c.contactos} · ${c.con_hora} con hora (${pct}%)`);
    fila.querySelector('.crm-barra-txt').prepend(icono(iconoCanal(c.canal)));
    bC.appendChild(fila);
  }
  if (!r.canales.length) bC.appendChild(el('p', 'agente-ayuda', 'Nadie ha escrito en este período.'));
  caja.appendChild(bC);

  // --- Servicio ---
  const bS = bloqueReporte('Por tipo de cita', 'ti-calendar',
    'Sale de las horas, no del embudo: una persona puede pedir dos tipos distintos.');
  const maxS = Math.max(1, ...r.servicios.map((s) => Number(s.horas)));
  for (const s of r.servicios) {
    bS.appendChild(barra(s.servicio, Number(s.horas), maxS,
      `${s.horas} horas · ${s.atendidas} atendidas · ${s.canceladas} canceladas · ${s.no_asistio} no asistió`));
  }
  if (!r.servicios.length) bS.appendChild(el('p', 'agente-ayuda', 'No hay horas en este período.'));
  caja.appendChild(bS);

  // --- Lo que no se está midiendo ---
  // ⚠️ Esto no es un descargo, es parte del reporte: un porcentaje de conversión sobre un historial
  // de dos días no es un porcentaje, y quien lo mira no tiene cómo saberlo si la pantalla no se lo
  // dice. Misma disciplina que los reportes de A.11.
  const a = r.alcance ?? {};
  const bN = bloqueReporte('Qué no se está midiendo', 'ti-info-circle');
  const lista = el('ul', 'crm-limites');
  const puntos = [
    a.historial_desde
      ? `Se registra desde el ${fecha(a.historial_desde)}: los períodos más largos miden menos días de los que dicen.`
      : 'Todavía no hay movimientos entre etapas: «entraron» y «días de media» van en cero.',
    `${a.sin_etapa ?? 0} de ${a.contactos ?? 0} contactos no tienen etapa y quedan fuera de este reporte.`,
    `${a.sin_canal ?? 0} contacto(s) no tienen ningún canal registrado y quedan fuera del reporte por canal.`,
    'El canal dice por dónde escribió, no de dónde vino.',
    'Alguien con dos canales cuenta en los dos.',
  ];
  for (const p of puntos) lista.appendChild(el('li', '', p));
  bN.appendChild(lista);
  caja.appendChild(bN);
}


/* ===========================================================================
   Datos: exportar, importar y tokens (C.6 y C.7)
   =========================================================================== */

/**
 * Un CSV no es «partir por comas».
 * Un nombre con coma, un campo entre comillas o un salto de línea dentro de una celda parten el
 * archivo en columnas equivocadas — y eso no falla, importa mal, que es peor. Este lector respeta
 * comillas, comillas dobladas y CRLF; es corto porque no acepta nada más exótico que eso.
 */
function leerCsv(texto) {
  const filas = [];
  let campo = '';
  let fila = [];
  let entreComillas = false;
  const limpio = texto.replace(/^\ufeff/, '');   // el BOM que escribe Excel
  for (let i = 0; i < limpio.length; i += 1) {
    const c = limpio[i];
    if (entreComillas) {
      if (c === '"') {
        if (limpio[i + 1] === '"') { campo += '"'; i += 1; } else entreComillas = false;
      } else campo += c;
    } else if (c === '"') entreComillas = true;
    else if (c === ',' || c === ';') { fila.push(campo); campo = ''; }
    else if (c === '\n') { fila.push(campo); filas.push(fila); fila = []; campo = ''; }
    else if (c !== '\r') campo += c;
  }
  if (campo !== '' || fila.length) { fila.push(campo); filas.push(fila); }
  return filas.filter((f) => f.some((x) => x.trim() !== ''));
}

// Los encabezados se comparan sin tildes ni mayúsculas: quien exporta desde Excel escribe «Teléfono»
// y quien lo escribe a mano, «telefono». Rechazar el archivo por una tilde sería absurdo.
const CLAVES = {
  telefono: 'telefono', celular: 'telefono', fono: 'telefono', movil: 'telefono',
  email: 'email', correo: 'email', mail: 'email',
  nombre: 'nombre', 'nombre completo': 'nombre',
  etapa: 'etapa', estado: 'etapa',
};
const sinTildes = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

function filasDesdeCsv(texto) {
  const bruto = leerCsv(texto);
  if (bruto.length < 2) return { error: 'El archivo necesita una fila de encabezados y al menos una fila de datos.' };
  const cabecera = bruto[0].map((h) => CLAVES[sinTildes(h)] ?? null);
  if (!cabecera.some(Boolean)) {
    return { error: 'Ninguna columna se reconoce. Se esperan: telefono, email, nombre o etapa.' };
  }
  const filas = bruto.slice(1).map((f) => {
    const o = {};
    cabecera.forEach((k, i) => { if (k && f[i]?.trim()) o[k] = f[i].trim(); });
    return o;
  }).filter((o) => Object.keys(o).length);
  return { filas };
}

const ETIQUETA = {
  actualizado: 'Se completa', sin_cambios: 'Ya estaba completo',
  no_encontrado: 'No existe (no se crea)', ambiguo: 'Cruza con varias fichas',
  sin_llave: 'Sin teléfono ni correo',
};

/** El simulacro y la ejecución muestran lo mismo; sólo cambia el botón. */
async function importar(filas, aplicar) {
  const caja = $('dat-previo');
  caja.textContent = aplicar ? 'Importando…' : 'Revisando…';
  let r;
  try {
    r = await apiFetch(API.importar, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filas, aplicar }),
      hideErrorToast: true,
    });
  } catch (e) { caja.textContent = ''; Toast.error(e?.mensaje || 'No se pudo importar.'); return; }

  caja.innerHTML = '';
  const cuenta = {};
  for (const f of r.filas) cuenta[f.resultado] = (cuenta[f.resultado] ?? 0) + 1;
  const resumen = Object.entries(cuenta)
    .map(([k, n]) => `${n} · ${ETIQUETA[k] ?? k}`).join('  ·  ');
  caja.appendChild(el('p', 'crm-resumen-import', resumen));

  const lista = el('div', 'cfg-lista');
  // Se muestran las 60 primeras: con 2.000 filas, pintarlas todas cuelga el navegador justo cuando
  // hay más que revisar.
  for (const f of r.filas.slice(0, 60)) {
    const fila = el('div', 'cfg-item');
    const izq = el('div');
    izq.appendChild(el('div', 'cfg-item-titulo', `Fila ${f.fila}: ${f.nombre ?? '(sin nombre)'}`));
    const sub = el('div', 'cfg-item-sub', `${ETIQUETA[f.resultado] ?? f.resultado} — ${f.detalle}`);
    if (f.resultado === 'no_encontrado' || f.resultado === 'ambiguo' || f.resultado === 'sin_llave') {
      sub.classList.add('crm-vencida');
    }
    izq.appendChild(sub);
    fila.appendChild(izq);
    lista.appendChild(fila);
  }
  if (r.filas.length > 60) {
    lista.appendChild(el('p', 'agente-ayuda', `…y ${plural(r.filas.length - 60, 'fila', 'filas')} más.`));
  }
  caja.appendChild(lista);

  if (aplicar) { Toast.success('Importación aplicada.'); await cargarContactos(); return; }

  const nAplicables = cuenta.actualizado ?? 0;
  const acciones = el('div', 'cfg-guardar');
  if (nAplicables > 0) {
    const b = el('button', 'ag-accion', `Aplicar a ${plural(nAplicables, 'contacto', 'contactos')}`);
    b.type = 'button';
    b.addEventListener('click', () => importar(filas, true));
    acciones.appendChild(b);
  } else {
    acciones.appendChild(el('p', 'agente-ayuda', 'No hay nada que completar con este archivo.'));
  }
  caja.appendChild(acciones);
}

/** La vista de Datos junta lo que se configura una vez y se olvida. */
async function cargarDatos() {
  await Promise.all([cargarTokens(), cargarSeguimiento(), cargarListas()]);
}

/* ===========================================================================
   Prioridad (C.11)
   ===========================================================================
   ⚠️ **Se muestran los motivos junto al nivel, siempre.** Un puntaje sin explicación es un veredicto
   que nadie puede discutir, y el día que marque «baja» a alguien que iba a comprar, el error se paga
   entero. Con el motivo a la vista, quien atiende decide. */
async function cargarPrioridad() {
  const caja = $('pri-lista');
  caja.textContent = 'Calculando…';
  let r;
  try { r = await apiFetch(API.prioridad, { credentials: 'include', hideErrorToast: true }); }
  catch { caja.textContent = 'No se pudo calcular.'; return; }
  caja.innerHTML = '';
  if (!r.contactos.length) {
    caja.appendChild(el('p', 'agente-ayuda', 'Todavía no hay contactos que ordenar.'));
    return;
  }
  for (const c of r.contactos) {
    const fila = el('div', 'cfg-item');
    const izq = el('div');
    const t = el('div', 'cfg-item-titulo');
    t.appendChild(el('span', '', c.nombre));
    const chip = el('span', `crm-nivel crm-nivel-${c.nivel}`, c.nivel);
    t.appendChild(chip);
    izq.appendChild(t);
    const partes = [];
    if (c.telefono) partes.push(telefonoLegible(c.telefono));
    if (c.etapa) partes.push(c.etapa);
    // Los motivos son la mitad del valor: sin ellos esto es un número que hay que creer.
    if (c.motivos?.length) partes.push(c.motivos.join(' · '));
    izq.appendChild(el('div', 'cfg-item-sub', partes.join('  —  ')));
    fila.appendChild(izq);
    const der = el('div', 'cfg-item-acciones');
    const b = el('button', 'btn-sec btn-sm', 'Ver ficha');
    b.type = 'button';
    b.addEventListener('click', () => verFicha(c.contacto_id));
    der.appendChild(b);
    fila.appendChild(der);
    caja.appendChild(fila);
  }
}

/* ===========================================================================
   Listas guardadas (C.12)
   =========================================================================== */

// Las condiciones se ofrecen como frases y no como un constructor de filtros: el negocio piensa en
// «los que no vuelven», no en `sin_actividad_dias >= 90`.
const CONDICIONES = [
  ['{"sin_actividad_dias":90}', 'No vuelven hace 3 meses'],
  ['{"sin_actividad_dias":180}', 'No vuelven hace 6 meses'],
  ['{"no_asistio":true}', 'Alguna vez no llegaron a su hora'],
  ['{"citas":false}', 'Nunca han agendado'],
  ['{"etapa":"sin"}', 'Sin etapa asignada'],
];

async function cargarListas() {
  const sel = $('lst-cond');
  if (sel && !sel.options.length) {
    for (const [v, t] of CONDICIONES) {
      const o = el('option', '', t); o.value = v;
      sel.appendChild(o);
    }
  }
  const caja = $('lst-lista');
  let r;
  try { r = await apiFetch(API.listas, { credentials: 'include', hideErrorToast: true }); }
  catch { caja.textContent = 'No se pudieron cargar.'; return; }
  caja.innerHTML = '';
  if (!r.listas.length) {
    caja.appendChild(el('p', 'agente-ayuda', 'Todavía no has guardado ninguna lista.'));
    return;
  }
  for (const l of r.listas) {
    const fila = el('div', 'cfg-item');
    const izq = el('div');
    izq.appendChild(el('div', 'cfg-item-titulo', l.nombre));
    // El conteo se recalcula al listar: una lista que dice 40 y trae 37 no sirve para nada.
    izq.appendChild(el('div', 'cfg-item-sub', `${l.cuantos} contacto(s) ahora mismo`));
    fila.appendChild(izq);
    const der = el('div', 'cfg-item-acciones');
    const bo = el('button', 'btn-sec btn-sm', 'Borrar');
    bo.type = 'button';
    bo.addEventListener('click', async () => {
      if (!window.confirm(`¿Borrar la lista «${l.nombre}»? No se borra ningún contacto.`)) return;
      try {
        await apiFetch(API.listaBorrar, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: l.id }),
          hideErrorToast: true,
        });
        await cargarListas();
      } catch (e) { Toast.error(e?.mensaje || 'No se pudo borrar.'); }
    });
    der.appendChild(bo);
    fila.appendChild(der);
    caja.appendChild(fila);
  }
}

/**
 * El seguimiento del que no cerró (C.10).
 *
 * ⚠️ **Se muestra encendido/apagado como un estado, no como un botón de "guardar y ya".** Es lo único
 * de este panel que le escribe a los clientes finales del negocio: tiene que quedar clarísimo si
 * está corriendo, y cuánto sirvió.
 */
async function cargarSeguimiento() {
  const caja = $('seg-caja');
  if (!caja) return;
  let r;
  try { r = await apiFetch(API.seguimiento, { credentials: 'include', hideErrorToast: true }); }
  catch { caja.textContent = 'No se pudo cargar.'; return; }
  const regla = (r.reglas ?? [])[0] ?? null;

  caja.innerHTML = '';
  const form = el('form', 'cfg-tarjeta');
  const campos = el('div', 'cfg-campos');
  form.appendChild(campos);
  const campo = (etiqueta, ctl, ayuda) => {
    const f = el('label', 'ag-campo');
    f.appendChild(el('span', '', etiqueta));
    f.appendChild(ctl);
    if (ayuda) f.appendChild(el('p', 'agente-ayuda', ayuda));
    campos.appendChild(f);
  };

  const mensaje = document.createElement('textarea');
  mensaje.rows = 3;
  mensaje.value = regla?.mensaje
    ?? '{nombre}, ¿alcanzaste a ver lo que conversamos? Si quieres te dejo una hora, dime qué día te acomoda.';
  campo('Qué se le escribe', mensaje,
    'Puedes usar {nombre} y se reemplaza por el nombre de la persona.');

  const espera = document.createElement('select');
  for (const [v, t] of [['24', 'Un día'], ['48', 'Dos días'], ['72', 'Tres días'],
    ['168', 'Una semana']]) {
    const o = el('option', '', t); o.value = v;
    if (String(regla?.espera_horas ?? 48) === v) o.selected = true;
    espera.appendChild(o);
  }
  campo('Después de cuánto silencio', espera);

  const intentos = document.createElement('select');
  for (const [v, t] of [['1', 'Una vez'], ['2', 'Dos veces']]) {
    const o = el('option', '', t); o.value = v;
    if (String(regla?.max_intentos ?? 2) === v) o.selected = true;
    intentos.appendChild(o);
  }
  // El tope de 2 no es un desplegable corto por casualidad: el tercero es spam y quema tu número.
  campo('Cuántas veces insistir', intentos, 'Como máximo dos.');

  if (regla && regla.toques > 0) {
    const res = el('p', 'agente-ayuda',
      `En 30 días: ${regla.toques} mensaje(s), ${regla.respondieron} respondieron y `
      + `${regla.agendaron} agendaron.`);
    form.appendChild(res);
  }

  const acciones = el('div', 'cfg-guardar');
  const estadoTxt = el('span', 'crm-seg-estado', regla?.activa ? 'Encendido' : 'Apagado');
  if (regla?.activa) estadoTxt.classList.add('on');
  acciones.appendChild(estadoTxt);
  const guardar = el('button', 'ag-accion', 'Guardar');
  guardar.type = 'submit';
  const alternar = el('button', 'btn-sec', regla?.activa ? 'Apagar' : 'Encender');
  alternar.type = 'button';
  acciones.append(alternar, guardar);
  form.appendChild(acciones);

  const enviar = async (activa) => {
    try {
      await apiFetch(API.seguimiento, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: regla?.id ?? null, mensaje: mensaje.value,
          espera: Number(espera.value), intentos: Number(intentos.value), activa,
        }),
        hideErrorToast: true,
      });
      Toast.success(activa ? 'Seguimiento encendido.' : 'Guardado.');
      await cargarSeguimiento();
    } catch (e) { Toast.error(e?.mensaje || 'No se pudo guardar.'); }
  };
  form.addEventListener('submit', (ev) => { ev.preventDefault(); enviar(regla?.activa ?? false); });
  alternar.addEventListener('click', () => enviar(!(regla?.activa ?? false)));
  caja.appendChild(form);
}

async function cargarTokens() {
  const caja = $('dat-tokens');
  let r;
  try { r = await apiFetch(API.tokens, { credentials: 'include', hideErrorToast: true }); }
  catch { caja.textContent = 'No se pudieron cargar los tokens.'; return; }
  caja.innerHTML = '';
  if (!r.tokens.length) {
    caja.appendChild(el('p', 'agente-ayuda', 'No tienes ningún token todavía.'));
    return;
  }
  for (const t of r.tokens) {
    const fila = el('div', 'cfg-item');
    const izq = el('div');
    izq.appendChild(el('div', 'cfg-item-titulo', t.nombre));
    izq.appendChild(el('div', 'cfg-item-sub',
      `${t.prefijo}…  ·  creado ${fechaCorta(t.creado_en)}  ·  ${
        t.ultimo_uso_en ? `último uso ${fechaCorta(t.ultimo_uso_en)}` : 'nunca se ha usado'}`));
    fila.appendChild(izq);
    const der = el('div', 'cfg-item-acciones');
    const b = el('button', 'btn-sec btn-sm', 'Revocar');
    b.type = 'button';
    b.addEventListener('click', async () => {
      if (!window.confirm(`¿Revocar «${t.nombre}»? Lo que use ese token deja de funcionar al instante.`)) return;
      try {
        await apiFetch(API.tokenRevocar, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: t.id }),
          hideErrorToast: true,
        });
        Toast.success('Token revocado.');
        await cargarTokens();
      } catch (e) { Toast.error(e?.mensaje || 'No se pudo revocar.'); }
    });
    der.appendChild(b);
    fila.appendChild(der);
    caja.appendChild(fila);
  }
}

/* ===========================================================================
   Ficha (panel lateral)
   =========================================================================== */

function cerrarFicha() {
  estado.fichaAbierta = null;
  $('crm-ficha').hidden = true;
  $('crm-fondo').hidden = true;
}

async function verFicha(id) {
  estado.fichaAbierta = id;
  const caja = $('crm-ficha');
  caja.hidden = false;
  $('crm-fondo').hidden = false;
  caja.textContent = 'Cargando…';
  let f;
  try {
    f = await apiFetch(`${API.contacto}?id=${encodeURIComponent(id)}`, { credentials: 'include' });
  } catch { caja.textContent = 'No se pudo cargar la ficha.'; return; }
  caja.innerHTML = '';

  const cab = el('header', 'crm-panel-cab');
  cab.appendChild(el('h2', '', f.contacto.nombre_completo || '(sin nombre)'));
  const cerrar = el('button', 'crm-panel-x', '×');
  cerrar.type = 'button';
  cerrar.setAttribute('aria-label', 'Cerrar');
  cerrar.addEventListener('click', cerrarFicha);
  cab.appendChild(cerrar);
  caja.appendChild(cab);

  const datos = [f.contacto.telefono, f.contacto.email].filter(Boolean);
  if (datos.length) caja.appendChild(el('p', 'crm-panel-sub', datos.join(' · ')));

  // La etapa arriba del todo: es el único dato de la ficha que se cambia, no que se lee.
  const fila = el('label', 'ag-campo');
  fila.appendChild(el('span', '', 'Etapa'));
  const sel = document.createElement('select');
  const vacia = el('option', '', 'Sin etapa'); vacia.value = '';
  sel.appendChild(vacia);
  for (const e of estado.etapas) {
    const o = el('option', '', e.nombre); o.value = e.id;
    if (e.id === f.contacto.etapa_id) o.selected = true;
    sel.appendChild(o);
  }
  sel.addEventListener('change', () => moverEtapa(f.contacto.id, sel.value));
  fila.appendChild(sel);
  caja.appendChild(fila);

  const seccion = (titulo, ico) => {
    const h = el('h3', 'crm-panel-h');
    h.appendChild(icono(ico));
    h.appendChild(document.createTextNode(` ${titulo}`));
    caja.appendChild(h);
    const d = el('div');
    caja.appendChild(d);
    return d;
  };
  const listar = (destino, filas, vacio) => {
    if (!filas.length) { destino.appendChild(el('p', 'agente-ayuda', vacio)); return; }
    const l = el('div', 'cfg-lista');
    for (const t of filas) l.appendChild(el('div', 'cfg-item', t));
    destino.appendChild(l);
  };

  const cC = seccion('Por dónde escribe', 'ti-plug-connected');
  if (!f.identidades?.length) cC.appendChild(el('p', 'agente-ayuda', 'Ningún canal registrado.'));
  else {
    const l = el('div', 'cfg-lista');
    for (const i of f.identidades) {
      const it = el('div', 'cfg-item');
      const d = el('div', 'crm-eta-fila');
      d.appendChild(icono(iconoCanal(i.canal)));
      // Se dice cuál está verificada: es lo que distingue un dato comprobado de uno tecleado, y es
      // la misma distinción con la que se decide si unir dos fichas.
      d.appendChild(el('span', '', `${i.identificador ?? '—'}${i.verificado ? ' · verificado' : ' · sin verificar'}`));
      it.appendChild(d);
      l.appendChild(it);
    }
    cC.appendChild(l);
  }

  listar(seccion('Conversaciones', 'ti-message-2'), (f.conversaciones ?? []).map(
    (c) => `${c.canal} · ${c.estado} · último mensaje ${fecha(c.ultimo)}`), 'Ninguna todavía.');
  listar(seccion('Horas', 'ti-calendar'), (f.citas ?? []).map(
    (c) => `${fecha(c.inicio)} · ${c.servicio} · ${c.profesional} · ${c.estado}`), 'Ninguna todavía.');

  await pintarTareasDe(seccion('Tareas', 'ti-clipboard-list'), id);
  await pintarNotas(seccion('Notas', 'ti-forms'), id);
}

/** Notas del contacto abierto. */
async function pintarNotas(caja, contactoId) {
  let r;
  try {
    r = await apiFetch(`${API.notas}?contacto=${encodeURIComponent(contactoId)}`,
      { credentials: 'include', hideErrorToast: true });
  } catch { caja.appendChild(el('p', 'agente-ayuda', 'No se pudieron cargar las notas.')); return; }

  const escribir = el('form', 'crm-nota-form');
  const ta = document.createElement('textarea');
  ta.rows = 2; ta.placeholder = 'Escribe una nota…'; ta.className = 'crm-nota-txt';
  const b = el('button', 'ag-accion', 'Anotar');
  b.type = 'submit';
  escribir.append(ta, b);
  escribir.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    if (!ta.value.trim()) return;
    try {
      await apiFetch(API.nota, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contacto: contactoId, cuerpo: ta.value }),
        hideErrorToast: true,
      });
      await verFicha(contactoId);
    } catch (e) { Toast.error(e?.mensaje || 'No se pudo guardar la nota.'); }
  });
  caja.appendChild(escribir);

  if (!r.notas.length) { caja.appendChild(el('p', 'agente-ayuda', 'Nadie ha anotado nada todavía.')); return; }
  const lista = el('div', 'cfg-lista');
  for (const n of r.notas) {
    const fila = el('div', 'cfg-item');
    const izq = el('div');
    izq.appendChild(el('div', 'crm-nota-cuerpo', n.cuerpo));
    // «editada» se muestra: una nota que cambió sin decirlo es la misma trampa que esto vino a
    // arreglar, sólo que más chica.
    const meta = [n.autor, fechaCorta(n.creado_en)];
    if (n.editado_en) meta.push('editada');
    izq.appendChild(el('div', 'cfg-item-sub', meta.join(' · ')));
    fila.appendChild(izq);
    const der = el('div', 'cfg-item-acciones');
    if (n.propia) {
      const ed = el('button', 'btn-sec btn-sm', 'Editar');
      ed.type = 'button';
      ed.addEventListener('click', async () => {
        const nuevo = window.prompt('Editar la nota:', n.cuerpo);
        if (nuevo === null || !nuevo.trim()) return;
        try {
          await apiFetch(API.nota, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: n.id, cuerpo: nuevo }),
            hideErrorToast: true,
          });
          await verFicha(contactoId);
        } catch (e) { Toast.error(e?.mensaje || 'No se pudo editar.'); }
      });
      der.appendChild(ed);
    }
    const bo = el('button', 'btn-sec btn-sm', 'Borrar');
    bo.type = 'button';
    bo.addEventListener('click', async () => {
      if (!window.confirm('¿Borrar esta nota? El texto se elimina y no se puede recuperar.')) return;
      try {
        await apiFetch(API.notaBorrar, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: n.id }),
          hideErrorToast: true,
        });
        await verFicha(contactoId);
      } catch (e) { Toast.error(e?.mensaje || 'No se pudo borrar.'); }
    });
    der.appendChild(bo);
    fila.appendChild(der);
    lista.appendChild(fila);
  }
  caja.appendChild(lista);
}

/** Tareas del contacto abierto. */
async function pintarTareasDe(caja, contactoId) {
  let r;
  try {
    r = await apiFetch(`${API.tareas}?contacto=${encodeURIComponent(contactoId)}`,
      { credentials: 'include', hideErrorToast: true });
  } catch { return; }
  const b = el('button', 'btn-sec btn-sm', '+ Tarea para este contacto');
  b.type = 'button';
  b.addEventListener('click', () => { cerrarFicha(); abrirEditorTarea(null, contactoId); });
  caja.appendChild(b);
  if (!r.tareas.length) { caja.appendChild(el('p', 'agente-ayuda', 'Nada pendiente con esta persona.')); return; }
  const lista = el('div', 'cfg-lista');
  for (const t of r.tareas) lista.appendChild(filaTarea(t, { mostrarContacto: false }));
  caja.appendChild(lista);
}

/* ===========================================================================
   Arranque
   =========================================================================== */

function conRetraso(fn, ms = 350) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

/** Un 401 acá manda al acceso del portal: el CRM no tiene login propio. */
async function iniciar() {
  try {
    await apiFetch(`${API.contactos}?limite=1`, { credentials: 'include', hideErrorToast: true });
  } catch (e) {
    if (e?.status === 401) { location.replace('https://app.deviant.cl/'); return; }
    $('crm-cargando').hidden = true;
    $('crm-aviso').textContent = 'No se pudo cargar el CRM. Reintenta en un momento.';
    $('crm-aviso').hidden = false;
    return;
  }
  $('crm-cargando').hidden = true;
  $('crm-app').hidden = false;

  try {
    estado.usuarios = (await apiFetch(API.usuarios,
      { credentials: 'include', hideErrorToast: true })).usuarios ?? [];
  } catch { estado.usuarios = []; }
  try {
    estado.canales = (await apiFetch(API.canales,
      { credentials: 'include', hideErrorToast: true })).canales ?? [];
  } catch { estado.canales = []; }
  // Las etapas y sus conteos se piden una vez al arrancar: los usan los filtros de contactos y el
  // selector de la ficha, no sólo el tablero.
  await cargarTablero();
  estado.cargadas.add('embudo');
  estado.cargadas.add('etapas');

  // --- Navegación ---
  for (const b of document.querySelectorAll('.crm-menu-item[data-vista]')) {
    b.addEventListener('click', () => irA(b.dataset.vista));
  }

  // --- Embudo ---
  $('emb-nueva').addEventListener('click', () => abrirEditor(null, 'crm-editor'));
  $('emb-buscar').addEventListener('input', conRetraso(() => {
    estado.tablero.q = $('emb-buscar').value.trim();
    cargarTablero();
  }));

  // --- Contactos ---
  const rehacer = () => { estado.contactos.pagina = 0; cargarContactos(); };
  $('con-buscar').addEventListener('input', conRetraso(rehacer));
  for (const id of ['con-etapa', 'con-canal', 'con-citas', 'con-orden']) {
    $(id).addEventListener('change', rehacer);
  }
  $('con-antes').addEventListener('click', () => {
    if (estado.contactos.pagina > 0) { estado.contactos.pagina -= 1; cargarContactos(); }
  });
  $('con-despues').addEventListener('click', () => {
    estado.contactos.pagina += 1;
    cargarContactos();
  });
  // La descarga va por navegación y no por `fetch`: la cookie de sesión viaja igual y el navegador
  // hace lo que sabe hacer con un `Content-Disposition`, sin pasar el archivo por memoria.
  $('con-exportar').addEventListener('click', () => { location.href = API.exportar; });

  // --- Tareas ---
  $('tar-nueva').addEventListener('click', () => abrirEditorTarea(null, null));
  $('tar-quien').addEventListener('change', cargarTareas);
  $('tar-cerradas').addEventListener('change', cargarTareas);

  // --- Datos ---
  $('dat-exportar').addEventListener('click', () => { location.href = API.exportar; });
  $('dat-archivo').addEventListener('change', async (ev) => {
    // El nombre lo escribe el `<span>` de al lado: el `<input>` va escondido (ver `dv-archivo`).
    $('dat-archivo-nombre').textContent = ev.target.files?.[0]?.name ?? 'Ninguno elegido';
    const archivo = ev.target.files?.[0];
    if (!archivo) return;
    // ⚠️ `archivo.text()` puede fallar y hay que decirlo. El navegador guarda una *referencia* al
    // archivo, no su contenido: si entremedio lo mueven, lo borran o vive en un disco que se
    // desmonto, la lectura lanza `NotFoundError`. Sin este `catch` era un rechazo sin manejar --
    // se iba a Sentry y en pantalla no pasaba nada, que es la peor forma de fallar en un boton
    // que alguien acaba de apretar.
    let texto;
    try { texto = await archivo.text(); } catch {
      $('dat-previo').textContent = '';
      Toast.error('No se pudo leer el archivo. Vuelve a elegirlo.');
      return;
    }
    const { filas, error } = filasDesdeCsv(texto);
    if (error) { $('dat-previo').textContent = ''; Toast.error(error); return; }
    if (!filas.length) { Toast.error('El archivo no tiene filas con datos.'); return; }
    // Siempre se revisa antes: este botón sube un archivo que alguien armó a mano.
    await importar(filas, false);
  });
  $('dat-token-form').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const nombre = $('dat-token-nombre').value.trim();
    if (!nombre) { Toast.error('Ponle un nombre para saber después para qué era.'); return; }
    let r;
    try {
      r = await apiFetch(API.token, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre }),
        hideErrorToast: true,
      });
    } catch (e) { Toast.error(e?.mensaje || 'No se pudo crear.'); return; }
    $('dat-token-nombre').value = '';
    // ⚠️ Se muestra una sola vez y la pantalla lo dice: en la base sólo queda el hash, así que no
    // hay forma de volver a enseñarlo. Callarlo sería que alguien cierre la caja y pierda el token.
    const caja = $('dat-token-nuevo');
    caja.hidden = false;
    caja.innerHTML = '';
    const aviso = el('div', 'agente-aviso');
    aviso.appendChild(icono('ti-key'));
    const txt = el('div');
    txt.appendChild(el('strong', '', 'Cópialo ahora: no se puede volver a ver.'));
    txt.appendChild(el('div', 'crm-token-valor', r.token));
    txt.appendChild(el('p', 'agente-ayuda', 'Guardamos sólo una huella. Si lo pierdes, crea otro y revoca éste.'));
    aviso.appendChild(txt);
    caja.appendChild(aviso);
    await cargarTokens();
  });

  $('lst-form').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const nombre = $('lst-nombre').value.trim();
    if (!nombre) { Toast.error('Ponle un nombre a la lista.'); return; }
    try {
      await apiFetch(API.lista, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre, definicion: JSON.parse($('lst-cond').value) }),
        hideErrorToast: true,
      });
      $('lst-nombre').value = '';
      Toast.success('Lista guardada.');
      await cargarListas();
    } catch (e) { Toast.error(e?.mensaje || 'No se pudo guardar.'); }
  });

  // --- Reportes y etapas ---
  $('rep-dias').addEventListener('change', cargarReportes);
  $('eta-nueva').addEventListener('click', () => abrirEditor(null, 'crm-editor-etapas'));

  // El tema de esta app. El global se cambia desde el panel: tenerlo repetido en cada pantalla
  // invita a cambiar "el de todas" creyendo que se cambia el de una.
  // La cabecera comun de escritorio (logo, las cinco apps, tema y cuenta). En un telefono no se
  // dibuja: lo decide `cabecera.css`, no un condicional aca.
  montarCabecera({ app: 'crm' });
  montarBotonTema($('tema-boton'));

  // --- Ficha ---
  $('crm-fondo').addEventListener('click', cerrarFicha);
  document.addEventListener('keydown', (ev) => { if (ev.key === 'Escape') cerrarFicha(); });

  $('crm-salir').addEventListener('click', async () => {
    try { await apiFetch(API.logout, { method: 'POST', credentials: 'include', hideErrorToast: true }); }
    catch { /* igual se sale */ }
    location.replace('https://app.deviant.cl/');
  });

  const inicial = location.hash.replace('#', '');
  await irA(['resumen', 'embudo', 'contactos', 'tareas', 'reportes', 'etapas', 'datos', 'prioridad'].includes(inicial)
    ? inicial : 'resumen');
}

iniciar();
